import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Position } from '@xyflow/react';
import { memo, useCallback, useMemo, useState } from 'react';

import { ConditionalRender } from '@/components/ConditionalRender';
import { useOpenEntityDrawer } from '@/hooks/useOpenEntityDrawer';
import { AddActionModal } from '@/sections/attacks/attackPaths/components/AddActionModal';
import { AttackGraphIcon } from '@/sections/attacks/attackPaths/components/icons';
import { NodeHandles } from '@/sections/attacks/attackPaths/components/nodes/NodeHandles';
import { AssetModal } from '@/sections/attacks/attackPaths/components/wizards/AssetModal';
import { techniqueColors } from '@/sections/attacks/attackPaths/constants';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import type {
  CustomAssetNode,
  StageAction,
} from '@/sections/attacks/attackPaths/types';
import { parseKeys } from '@/sections/SearchByType';
import { cn } from '@/utils/classname';
import { generateUuid } from '@/utils/uuid.util';

interface AssetNodeComponentProps {
  data: CustomAssetNode['data'];
  id: string;
  sourcePosition?: Position;
  targetPosition?: Position;
}

function renderIcon(iconOverride?: string) {
  return (
    <AttackGraphIcon
      icon={iconOverride ?? ''}
      size={32}
      className={
        !iconOverride || iconOverride === ''
          ? 'fill-chariot-text-primary size-8'
          : 'text-chariot-text-primary size-8'
      }
    />
  );
}

function AssetNodeComponent({
  data,
  id,
  sourcePosition,
  targetPosition,
}: AssetNodeComponentProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addActionModalOpen, setAddActionModalOpen] = useState(false);
  const {
    removeNode,
    isEditMode,
    stages,
    setStages,
    nodes,
    focusedStageIndex,
  } = useAttackState();
  const { openDrawerByKey } = useOpenEntityDrawer();

  // Collect actions from all stages that target this asset, tagged with stage index
  const actions = useMemo(() => {
    const result: (StageAction & { _stageIndex: number })[] = [];
    if (!stages) return result;
    for (let si = 0; si < stages.length; si++) {
      const stage = stages[si];
      for (const action of stage.actions || []) {
        if (action.assetNodeId === id) {
          result.push({ ...action, _stageIndex: si });
        }
      }
    }
    return result;
  }, [stages, id]);

  const assetNodes = useMemo(
    () => nodes.filter((n): n is CustomAssetNode => n.type === 'asset'),
    [nodes]
  );

  const hasActions = actions.length > 0;

  const handleAddAction = useCallback(
    (actionData: Omit<StageAction, 'id'>) => {
      setStages(prev => {
        let targetStage = prev[0];
        let updatedStages = [...prev];
        if (!targetStage) {
          targetStage = {
            id: generateUuid(),
            name: 'Stage 1',
            techniqueIds: [],
            result: 'untested' as const,
            summary: '',
            details: '',
            artifacts: [],
            actions: [],
          };
          updatedStages = [targetStage];
        }
        const newAction = {
          ...actionData,
          id: generateUuid(),
          assetNodeId: actionData.assetNodeId || id,
        };
        return updatedStages.map(s =>
          s.id === targetStage.id
            ? { ...s, actions: [...(s.actions || []), newAction] }
            : s
        );
      });
    },
    [id, setStages]
  );

  // Tolerate the alias shape Marcus sometimes emits (data.key/data.label) in
  // addition to the canonical data.id/data.displayName; otherwise alias-shaped
  // asset nodes render blank and aren't clickable.
  const assetKey = data?.id || data?.key;
  const { key: isAssetKey, identifier: assetIdentifier } =
    !data.type && assetKey ? parseKeys.assetKey(assetKey) : {};

  const nodeForEdit: CustomAssetNode = {
    id,
    type: 'asset',
    position: { x: 0, y: 0 },
    data,
  };

  const handleEditClick = () => {
    if (isEditMode) {
      setEditModalOpen(true);
    }
  };

  return (
    <>
      {/* w-[160px] == getNodeDimensions('asset').width: keep the box at the reserved width so a wide on-host actions card overflows centered (cross-axis isn't shrunk) instead of shifting the circle/handle off dagre's column center — visible as slanted edges in TB/vertical mode. */}
      <div className="flex w-[160px] flex-col items-center">
        <ConditionalRender
          condition={isEditMode}
          conditionalWrapper={children => (
            <div
              role="button"
              tabIndex={0}
              onClick={handleEditClick}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleEditClick();
                }
              }}
              className="cursor-pointer"
              aria-label="Edit asset node"
            >
              {children}
            </div>
          )}
          elseWrapper={children => (
            <ConditionalRender
              condition={Boolean(isAssetKey)}
              conditionalWrapper={innerChildren => (
                <button
                  type="button"
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  onClick={() => assetKey && openDrawerByKey(assetKey, 'asset')}
                  aria-label="View asset details"
                >
                  {innerChildren}
                </button>
              )}
            >
              {children}
            </ConditionalRender>
          )}
        >
          {/* (130-72)/2=29px top spacer centers the 72px circle's handle at dims.height/2=65 (see getNodeDimensions in utils/computeLayout.ts) */}
          <div className="relative flex w-[160px] flex-col items-center pt-[29px]">
            <div className="border-chariot-stroke bg-chariot-background-secondary relative flex size-[72px] items-center justify-center rounded-full border-2">
              {renderIcon(data.iconOverride)}
              {isEditMode && (
                <button
                  className="bg-chariot-status-critical hover:bg-chariot-status-critical/80 absolute -top-1 -right-1 rounded-full p-[2px]"
                  onClick={event => {
                    event.stopPropagation();
                    removeNode(id);
                  }}
                  aria-label="Remove node"
                >
                  <XMarkIcon className="text-chariot-text-primary size-3 stroke-[4px]" />
                </button>
              )}
              {isEditMode && (
                <button
                  className={cn(
                    'absolute -bottom-1 -left-1',
                    'bg-chariot-brand-primary/20 border-chariot-brand-primary/40 flex size-5 items-center justify-center rounded-full border',
                    'text-chariot-brand-primary cursor-pointer transition-transform hover:scale-110'
                  )}
                  onClick={e => {
                    e.stopPropagation();
                    setAddActionModalOpen(true);
                  }}
                  aria-label="Add on-host action"
                  title="Add on-host action"
                >
                  <PlusIcon className="size-3" />
                </button>
              )}

              <NodeHandles
                isEditMode={isEditMode}
                nodeId={id}
                sourcePosition={sourcePosition}
                targetPosition={targetPosition}
              />
            </div>
            <div className="mt-2 w-max min-w-full text-center text-sm font-medium wrap-break-word">
              {data?.displayName ||
                data?.label ||
                (isAssetKey ? assetIdentifier : assetKey)}
            </div>
            {data.metadata && (
              <div className="text-chariot-text-secondary mt-0.5 w-max min-w-full text-center text-[10px] wrap-break-word">
                {data.metadata}
              </div>
            )}
          </div>
        </ConditionalRender>

        {/* On-host actions — compact card list */}
        {hasActions && (
          <div className="border-chariot-stroke bg-chariot-background-secondary/80 mt-2 w-max max-w-[220px] min-w-[140px] rounded-md border backdrop-blur-xs">
            {actions.map((action, i) => {
              const colors = techniqueColors[action.status || ''];
              const isLast = i === actions.length - 1;
              const isDimmed =
                focusedStageIndex !== null &&
                action._stageIndex !== focusedStageIndex;

              return (
                <div
                  key={action.id}
                  className={cn(
                    'flex items-start gap-2 px-2.5 py-1.5 transition-opacity',
                    !isLast && 'border-chariot-stroke/50 border-b',
                    isDimmed && 'opacity-20'
                  )}
                >
                  {/* Status dot */}
                  <span
                    className={cn(
                      'mt-1 size-1.5 shrink-0 rounded-full',
                      colors?.dot || 'bg-chariot-text-tertiary'
                    )}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="text-chariot-text-primary text-[11px] leading-tight font-medium">
                      {action.label}
                    </span>
                    {action.techniqueId && (
                      <span className="text-chariot-text-tertiary text-[9px]">
                        {action.techniqueId}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AssetModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        mode="edit"
        initialNode={nodeForEdit}
      />

      <AddActionModal
        open={addActionModalOpen}
        onClose={() => setAddActionModalOpen(false)}
        assetNodes={assetNodes}
        defaultAssetNodeId={id}
        onSubmit={handleAddAction}
      />
    </>
  );
}

export const AssetNode = memo(AssetNodeComponent);
